import React, { useState, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Breadcrumb, Col, Row } from 'antd';

import Product from 'classes/Product';
import AlertComponent from 'components/Alert';
import { EProductStatus } from 'helpers/enums';
import { EPartnerCode } from 'interfaces/IProduct';
import type IProduct from 'interfaces/IProduct';
import Delivery from 'scenes/Producer/Product/components/InfoDeliveryMethods';
import General from 'scenes/Producer/Product/NewProduct/components/Forms/General';
import GeneralValidations from 'scenes/Producer/Product/NewProduct/components/Forms/General/validations';
import Sales from 'scenes/Producer/Product/NewProduct/components/Forms/Sales';
import SalesSubscriptionValidations from 'scenes/Producer/Product/NewProduct/components/Forms/Sales/components/Subscription/validations';
import SalesValidations from 'scenes/Producer/Product/NewProduct/components/Forms/Sales/validations';
import RecoverySalesValidation from 'scenes/Producer/Product/NewProduct/components/Forms/SalesRecovery/validations';
import TopPage from 'v2/components/Shared/TopPage';
import { getProductTypeTitle } from 'v2/helpers/productTypeTitle';
import { useCategories } from 'v2/hooks/products/useCategories';
import { useValidateProductTitle } from 'v2/hooks/products/useValidateProductTitle';
import { usePostMessage } from 'v2/hooks/usePostMessage';
import { PostMessageAction, PostMessageSource } from 'v2/interfaces/post-message';
import chatBotService, { Activity } from 'v2/services/chatBot';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import productService from 'v2/services/product';
import { useUserStore } from 'v2/store/User';
import { run } from 'validations/ruleRunner';

import DraftButtons from '../../components/DraftButtons';
import { NewProductHint } from '../../components/Hints/NewProductHint';
import SalesRecovery from '../components/Forms/SalesRecovery';
import { useWizardState } from '../contexts/WizardStateContext';

function ServiceProduct() {
  const history = useHistory();
  const { previousStep, updateLoading, resetLoading, setProduct: setWizardProduct, nextStep } = useWizardState();
  const { categories } = useCategories();
  const { productTitleExists, validateTitle: validateTitleForSave } = useValidateProductTitle();
  const sendPostMessage = usePostMessage();
  const { user } = useUserStore(({ user }) => ({ user }));

  const [product, setProduct] = useState<IProduct>(
    () => new Product({ type: 2, cnt_partner_cod: EPartnerCode.SERVICE, sales_recovery: true })
  );
  const [enabledValidation, setEnabledValidation] = useState(false);
  const [categoryLabel] = useState<any>(null);

  const errorsRef = useRef<any>({});
  const fieldsRef = useRef<any>({});

  const setRefField = useCallback((name: any, ref: any) => {
    fieldsRef.current[name] = ref;
  }, []);
  const setRefError = useCallback((name: any, ref: any) => {
    errorsRef.current[name] = ref;
  }, []);
  const handleChange = useCallback((name: string, value: any) => {
    setProduct(prev => {
      const c = new Product(prev);
      (c as Record<string, any>)[name] = value;
      return c;
    });
  }, []);
  const handleChangeDeliveries = useCallback((deliveries: any) => {
    setProduct(prev => {
      const p: IProduct = new Product(prev);
      p.setDeliveries(deliveries);
      return p;
    });
  }, []);

  const isFormValid = useCallback((productToValidate: IProduct): boolean => {
    const GeneralErrors = run(productToValidate, GeneralValidations);
    const generalRequiredFields = GeneralErrors ? Object.keys(GeneralErrors).length : 0;
    let salesRequiredFields = 0;

    if (productToValidate?.charge_type === 'N') {
      const e = run(productToValidate, SalesValidations);
      salesRequiredFields += e ? Object.keys(e).length : 0;
    }

    if (productToValidate?.charge_type === 'A') {
      const e = run(productToValidate, SalesSubscriptionValidations);
      salesRequiredFields += e ? Object.keys(e).length : 0;
    }

    if (productToValidate?.sales_recovery) {
      const e = run(productToValidate, RecoverySalesValidation);
      salesRequiredFields += e ? Object.keys(e).length : 0;
    }

    return generalRequiredFields === 0 && salesRequiredFields === 0;
  }, []);

  const backWizard = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      previousStep();
    },
    [previousStep]
  );

  const handleSubmit = useCallback(
    (isDraft?: boolean) => {
      const productToSubmit = product;

      if (isDraft) {
        productToSubmit.active = EProductStatus.DRAFT;
      }

      resetLoading();

      if (!isDraft && !isFormValid(productToSubmit)) {
        setEnabledValidation(true);
        toast.error('Por favor preencha todos os campos obrigatórios');
        return;
      }

      (async () => {
        try {
          updateLoading(33);
          const createdData = await productService.createProduct(productToSubmit.serviceFormat());
          const prod = createdData?.product ?? createdData;

          if (!prod?.id) {
            toast.error('Erro ao criar o produto');
            resetLoading();
            return;
          }

          if (isDraft) {
            sendPostMessage({
              source: PostMessageSource.PRODUCT_MANAGER,
              action: PostMessageAction.PRODUCT_CREATED,
              data: {
                product_id: prod.id,
                is_draft: true,
                completed_at: new Date().toISOString(),
                product_type: productToSubmit.type,
                product_type_title: getProductTypeTitle(productToSubmit.type)
              }
            });
            toast.success('Seu produto foi salvo como rascunho!');
            history.push('/producer/product/myproducts');
            return;
          }

          updateLoading(66);
          const loadResult: any = await productService.loadProductById(prod.id, false);
          const newProd = new Product(loadResult?.data ?? loadResult);
          setWizardProduct(newProd);
          updateLoading(100);
          resetLoading();
          nextStep();

          if (!user.producer) {
            eduzzAccountsService.loadAccountDetails();
          }
        } catch (error) {
          toast.error(error);
          resetLoading();
          return;
        }

        chatBotService.activity(Activity.productRegistered);
      })();
    },
    [product, isFormValid, user, history, resetLoading, updateLoading, setWizardProduct, nextStep, sendPostMessage]
  );

  const handleClickSubmit = useCallback(async () => {
    if (!isFormValid(product)) {
      setEnabledValidation(true);
      toast.error('Por favor preencha todos os campos obrigatórios');
      return;
    }

    const message = (
      <>
        Concordo que meu produto está em conformidade com as recomendações para evitar interrupções nas vendas durante o
        processo de análise e moderação.
        <Alert
          icon={<InfoCircleOutlined style={{ fontSize: 24 }} />}
          type='info'
          className='mt-3'
          message={
            <>
              Caso seja reprovado na moderação, seu produto ficará indisponível para venda.{' '}
              <a
                href='https://ajuda.eduzz.com/hc/pt-br/articles/1260807655450-Como-funciona-a-modera%C3%A7%C3%A3o-de-conte%C3%BAdo'
                rel='noopener noreferrer'
                target='_blank'
              >
                Saiba mais
              </a>
            </>
          }
        />
      </>
    );
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será enviado para moderação, mas não se preocupe, você já pode começar a vender.',
      message,
      confirmation: true,
      buttonOk: 'Confirmar e cadastrar',
      size: 'md'
    });

    if (confirm) {
      handleSubmit();
    }
  }, [handleSubmit, isFormValid, product]);

  const handleSubmitDraft = useCallback(async () => {
    const confirm = await AlertComponent.show({
      title: 'Quase lá...',
      caption: 'Seu produto será salvo como rascunho. Para publicar, volte a editar esse produto.',
      message: 'O seu produto não poderá ser vendido enquanto você não publicá-lo para venda. Deseja Continuar?',
      confirmation: true,
      buttonOk: 'Confirmar e cadastrar',
      size: 'md'
    });
    if (confirm) handleSubmit(true);
  }, [handleSubmit]);

  const isService = product.cnt_partner_cod === EPartnerCode.SERVICE;
  const canSaveDraft = !!product.title;

  return (
    <Row id='new-service-product' gutter={[16, 16]}>
      <Col span={24}>
        <Breadcrumb
          items={[
            { title: <Link to='/producer/product/myproducts'>Meus Produtos</Link> },
            {
              title: (
                <a href='#' style={{ cursor: 'pointer' }} onClick={backWizard}>
                  Tipo de produto
                </a>
              )
            },
            { title: 'Novo serviço' }
          ]}
        />
      </Col>
      <Col span={24}>
        <TopPage title='Novo Serviço Online' />
      </Col>

      <Col md={18} xs={24}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <General
              setRefField={setRefField}
              setRefError={setRefError}
              product={product}
              handleChange={handleChange}
              categoryList={categories}
              categorySelected={categoryLabel}
              validateTitleForSave={validateTitleForSave}
              productTitleExists={productTitleExists}
              enabledValidation={enabledValidation}
            />
          </Col>
          <Col span={24}>
            <Sales
              ref={(ref: any) => setRefField('sales', ref)}
              product={product}
              handleChange={handleChange}
              setRefField={setRefField}
              setRefError={setRefError}
              enabledValidation={enabledValidation}
            />
          </Col>
          <Col span={24}>
            <SalesRecovery handleChange={handleChange} product={product} />
          </Col>
          <Col span={24}>
            <Delivery
              ref={(ref: any) => setRefField('deliveries', ref)}
              newProduct
              fade
              deliveries={product ? product.deliveries : []}
              onChange={handleChangeDeliveries}
              offer={product}
              isService
            />
          </Col>
          <Col span={24}>
            <DraftButtons
              isService={isService}
              disabled={{ tooltip: false, button: false }}
              disabledDraft={{ tooltip: !!canSaveDraft, button: !canSaveDraft }}
              onSubmit={() => handleClickSubmit()}
              onSubmitDraft={() => handleSubmitDraft()}
            />
          </Col>
        </Row>
      </Col>
      <Col md={6} xs={24}>
        <NewProductHint type='service' />
      </Col>
    </Row>
  );
}

export default ServiceProduct;
